<template>
  <LayoutAuthPrimary>
    <div :style="style.container">
      <HeaderLargePrimary
        v-if="state.tenantInvitationCode"
        :truncate="true"
        :style="style.outerHeader"
      >
        Join {{ state.tenant.name }} workspace.
      </HeaderLargePrimary>
      <CardSecondary :style="getCardStyle">
        <div :style="style.header">
          <HeaderLargePrimary>
            {{ headerText }}
          </HeaderLargePrimary>
          <div
            v-if="confirmed"
            :style="style.subHeader"
          >
            <TextRegular :isInline="true">
              Already using Zipteam?
            </TextRegular>
            <LinkPrimary
              :isInline="true"
              :to="{ name: 'login' }"
            >
              Log in<!-- Avoid auto-added whitespace
         --></LinkPrimary><!--
         --><TextRegular :isInline="true"><!--
           -->.<!--
         --></TextRegular>
          </div>
        </div>
        <template v-if="!confirmed">
          <ImagePrimary
            :disableCursor="true"
            :src="require(`@img/verified.png`)"
            :style="style.image"
          />
          <ButtonWidePrimary @click="confirmVerification">
            Continue
          </ButtonWidePrimary>
        </template>
        <template v-else>
          <BeatLoaderPrimary v-if="isRequesting" />
          <FormSignupPrimary
            v-else
            :form.sync="form"
            :requirePassword="requirePassword"
          />
          <ButtonWidePrimary
            :disabled="disableSignup"
            @click="signup"
            :style="style.button"
          >
            Complete sign up
          </ButtonWidePrimary>
        </template>
      </CardSecondary>
    </div>
  </LayoutAuthPrimary>
</template>

<script>
  import ApiNewUser from '@js/api/new-user'
  import BeatLoaderPrimary from '@modules/v2/loader/beat-loader-primary'
  import ButtonWidePrimary from '@modules/v2/button/button-wide-primary'
  import CardSecondary from '@modules/v2/card/card-secondary'
  import FormSignupPrimary from '@modules/v2/form/form-signup-primary'
  import HeaderLargePrimary from '@modules/v2/header/header-large-primary'
  import HttpStatus from 'http-status-codes'
  import ImagePrimary from '@modules/v2/image/image-primary'
  import LayoutAuthPrimary from '@modules/v2/layout/layout-auth-primary'
  import LinkPrimary from '@modules/v2/link/link-primary'
  import TextRegular from '@modules/v2/text/text-regular'
  import { FormSignup } from '@js/forms/signup'
  import { Layout } from '@js/styles'

  export default {
    name: 'SignupView',
    components: {
      BeatLoaderPrimary,
      ButtonWidePrimary,
      CardSecondary,
      FormSignupPrimary,
      HeaderLargePrimary,
      ImagePrimary,
      LayoutAuthPrimary,
      LinkPrimary,
      TextRegular,
    },
    props: {
      state: {
        type: Object,
        required: false,
        default: () => {},
      },
    },
    data: function () {
      return {
        confirmed: false,
        errors: [],
        form: new FormSignup(),
        isRequesting: false,
        style: {
          button: {
            margin: `${Layout.space.lg} 0 0 0`,
            padding: `0 ${Layout.space.sm}`,
          },
          container: {
            alignItems: 'center',
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            justifyContent: 'center',
            padding: `${Layout.space.lg} 0`,
            width: '100%',
          },
          header: {
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
          },
          image: {
            margin: `${Layout.space.md} 0`,
            width: '200px',
          },
          outerHeader: {
            justifyContent: 'center',
            margin: `0 0 40px 0`,
            padding: `0 ${Layout.space.md}`,
            width: '100%',
          },
          subHeader: {
            margin: `${Layout.space.md} 0 0 0`,
          },
        },
      };
    },
    created: function () {
      if (!this.state.email) {
        this.$store.commit('notification/setWarning', 'Email not found.');
        this.$router.push({ name: 'signup' });
      } else if (
        !this.state.emailVerificationCode &&
        !this.state.tenantInvitationCode
      ) {
        this.$store.commit('notification/setWarning', 'Invalid URL.');
        this.$router.push({ name: 'signup' });
      } else if (
        !this.state.emailAvailability.isAvailable
      ) {
        this.$router.push({ name: 'login-continue', query: this.$route.query });
      }

      this.initializeForm();
    },
    computed: {
      disableSignup: function () {
        return this.form.errors.length > 0 || this.isRequesting;
      },
      getCardStyle: function () {
        return {
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: 'fit-content',
          //maxHeight: this.requirePassword ? '560px' : '400px',
          maxWidth: '400px',
          minHeight: this.requirePassword ? '560px' : '400px',
          padding: `${Layout.space.xl} ${Layout.space.md}`,
          width: '90%',
        };
      },
      headerText: function () {
        return (
          this.confirmed
            ? 'You are almost there...'
            : 'Your email is verified.'
        );
      },
      isInvited: function () {
        return this.state.tenantInvitationCode.length > 0;
      },
      requirePassword: function () {
        return !this.state.ssoCode;
      },
    },
    methods: {
      confirmVerification: function () {
        this.confirmed = true;
      },
      handleError: function (error) {
        console.error(error);

        if (error?.response?.status === HttpStatus.GONE) {
          const errorMessage = error?.response?.data?.messages[0];
          this.$store.commit('notification/setWarning', errorMessage);
        } else {
          this.$store.commit('notification/setWarning', 'Failed to signup.');
        }
      },
      initializeForm: function () {
        const keys = [
          'email',
          'emailVerificationCode',
          'tenantInvitationCode',
          'ssoCode',
        ];
        keys.forEach(key => {
          this.form.data[key] = this.state[key];
        });
      },
      signup: async function () {
        try {
          this.isRequesting = await true;
          const payload = await this.form.getPostPayload();
          const query = await {
            invitationCode: this.state.tenantInvitationCode,
          };

          const api = await new ApiNewUser();
          const res = await api.post(payload, query);

          await this.$store.commit('auth/setTokenPair', res.data.tokenPair);
          await this.$router.push({ name: 'tenants' });
          await this.$store.commit(
            'notification/setMessage',
            'Successfully joined the workspace',
          );
        } catch (error) {
          if (error?.response?.status === HttpStatus.GONE) {
            this.$store.commit(
              'notification/setWarning',
              error?.response?.data?.messages[0],
            );
          } else {
            this.handleError(error);
          }
        } finally {
          this.isRequesting = false;
        }
      },
    },
  };
</script>
